import { API_V1_BASE, API_V1PG_BASE, API_ROOT } from './constants';
import { attemptTokenRefresh, getAccessToken, notifyUnauthorized } from '../auth/session';

const buildUrl = (baseUrl, endpoint, queryParams) => {
    const normalizedBase = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
    const normalizedEndpoint = endpoint ? endpoint.replace(/^\//, '') : '';
    const url = new URL(`${normalizedBase}/${normalizedEndpoint}`);
    if (queryParams) {
        Object.entries(queryParams).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
                url.searchParams.append(key, value);
            }
        });
    }
    return url.toString();
};

const parseResponse = async (response) => {
    if (response.status === 204) {
        return {};
    }

    const text = await response.text();
    const payload = text ? JSON.parse(text) : null;

    if (!response.ok) {
        const message = payload?.error || payload?.details || payload?.message || response.statusText;
        const error = new Error(message || `Request failed with status ${response.status}`);
        error.status = response.status;
        error.payload = payload;
        throw error;
    }

    if (payload?.success === false) {
        const message = payload?.error || 'Request failed';
        const error = new Error(message);
        error.status = response.status;
        error.payload = payload;
        throw error;
    }

    return payload?.data ?? payload ?? {};
};

const send = async (baseUrl, endpoint, options = {}, attempt = 0) => {
    const {
        method = 'GET',
        body,
        queryParams,
        headers = {},
        skipAuth = false,
    } = options;

    const requestHeaders = {
        Accept: 'application/json',
        ...headers,
    };

    if (body !== undefined && body !== null) {
        requestHeaders['Content-Type'] = requestHeaders['Content-Type'] || 'application/json';
    }

    if (!skipAuth) {
        const token = getAccessToken();
        if (token) {
            requestHeaders.Authorization = `Bearer ${token}`;
        }
    }

    const response = await fetch(buildUrl(baseUrl, endpoint, queryParams), {
        method,
        headers: requestHeaders,
        body: body !== undefined && body !== null ? JSON.stringify(body) : undefined,
    });

    if (response.status === 401 && !skipAuth && attempt === 0) {
        try {
            await attemptTokenRefresh();
            return send(baseUrl, endpoint, options, attempt + 1);
        } catch (refreshError) {
            notifyUnauthorized();
            throw refreshError;
        }
    }

    if (response.status === 403) {
        try {
            const payload = await response.clone().json();
            const error = new Error(payload?.error || 'Forbidden');
            error.status = 403;
            error.payload = payload;
            throw error;
        } catch (parseError) {
            const error = new Error('Forbidden');
            error.status = 403;
            throw error;
        }
    }

    return parseResponse(response);
};

const requestV1 = (endpoint, options) => send(API_V1_BASE, endpoint, options);
const requestPg = (endpoint, options) => send(API_V1PG_BASE, endpoint, options);

const normalizeBand = (band) => {
    if (band === undefined || band === null) {
        return undefined;
    }
    const value = String(band).toLowerCase();
    if (['24', '2.4', '24ghz', 'aps_24'].includes(value)) {
        return '24';
    }
    if (['50', '5', '5.0', '5ghz', 'aps_50'].includes(value)) {
        return '50';
    }
    return String(band);
};

const normalizeBssid = (bssid) => {
    if (!bssid) {
        return bssid;
    }
    return bssid.toUpperCase().replace(/-/g, ':').replace(/\s+/g, '');
};

const normalizeApPayload = (payload = {}) => {
    const normalized = {
        ...payload,
        band: normalizeBand(payload.band),
        bssid: normalizeBssid(payload.bssid),
    };

    if (normalized.house_id !== undefined && normalized.house_id !== null && normalized.house_id !== '') {
        const parsed = Number(normalized.house_id);
        if (Number.isNaN(parsed)) {
            throw new Error('house_id must be a valid number');
        }
        normalized.house_id = parsed;
    }

    return normalized;
};

export const apiClient = {
    requestV1,
    requestPg,

    async get(endpoint, queryParams) {
        return requestPg(endpoint, { queryParams });
    },

    async post(endpoint, body) {
        return requestPg(endpoint, {
            method: 'POST',
            body
        });
    },

    async login(email, password) {
        return requestPg('auth/login', {
            method: 'POST',
            body: { email, password },
            skipAuth: true,
        });
    },

    async register(payload) {
        return requestPg('auth/register', {
            method: 'POST',
            body: payload,
            skipAuth: true,
        });
    },

    async refreshToken(refreshToken) {
        return requestPg('auth/refresh', {
            method: 'POST',
            body: { refresh_token: refreshToken },
            skipAuth: true,
        });
    },

    async changePassword(payload) {
        return requestPg('auth/change-password', {
            method: 'POST',
            body: payload,
        });
    },

    async getUsers() {
        const data = await requestPg('users');
        return data?.users ?? [];
    },

    async upsertUser(user) {
        const { user_id, password, confirmPassword, ...rest } = user;
        const hasId = Boolean(user_id);
        const method = hasId ? 'PUT' : 'POST';
        const endpoint = hasId ? `users/${encodeURIComponent(user_id)}` : 'users';
        const payload = { ...rest };
        if (password) {
            payload.password = password;
        }
        return requestPg(endpoint, {
            method,
            body: hasId ? payload : { user_id, ...payload },
        });
    },

    async getUserIsp(userId) {
        return requestPg(`users/${encodeURIComponent(userId)}/isp`);
    },

    async upsertUserIsp(userId, payload, method = 'PUT') {
        return requestPg(`users/${encodeURIComponent(userId)}/isp`, {
            method,
            body: payload,
        });
    },

    async deleteUserIsp(userId) {
        return requestPg(`users/${encodeURIComponent(userId)}/isp`, {
            method: 'DELETE',
        });
    },


    async getDeployments(userId) {
        const endpoint = userId ? `deployments/${encodeURIComponent(userId)}` : 'deployments';
        const data = await requestPg(endpoint);
        return data?.deployments ?? [];
    },

    async upsertDeployment(payload, isUpdate) {
        const { deployment_id, mac } = payload;
        const normalizedMac = mac ? mac.toUpperCase() : undefined;
        const body = normalizedMac ? { ...payload, mac: normalizedMac } : { ...payload };
        const identifier = isUpdate ? deployment_id : null;
        const endpoint = identifier ? `deployments/${encodeURIComponent(identifier)}` : 'deployments';
        const method = identifier ? 'PUT' : 'POST';
        return requestPg(endpoint, {
            method,
            body,
        });
    },

    async getDevices() {
        const data = await requestPg('devices');
        return data?.devices ?? [];
    },

    async upsertDevice(payload) {
        const { device_id, mac } = payload;
        const normalizedMac = mac ? mac.toUpperCase() : undefined;
        const body = normalizedMac ? { ...payload, mac: normalizedMac } : { ...payload };
        const method = device_id ? 'PUT' : 'POST';
        if (method === 'PUT' && !normalizedMac && !device_id) {
            throw new Error('Device updates require a MAC address.');
        }
        const identifier = method === 'PUT' ? (normalizedMac || device_id) : null;
        const endpoint = identifier ? `devices/${encodeURIComponent(identifier)}` : 'devices';
        return requestPg(endpoint, {
            method,
            body,
        });
    },

    async getWifiData(mac, interval, extraParams = {}) {
        const data = await requestV1('wifi', {
            queryParams: {
                mac: mac?.toUpperCase(),
                interval,
                ...extraParams,
            },
        });
        return data ?? {};
    },

    async getChUtilMetrics(mac, interval, extraParams = {}) {
        const data = await requestV1('wifi/ch-util', {
            queryParams: {
                mac: mac?.toUpperCase(),
                interval,
                ...extraParams,
            },
        });
        return data ?? {};
    },

    async getTxRxRetriesData(mac, interval, extraParams = {}) {
        const data = await requestV1('wifi/tx-rx-retries', {
            queryParams: {
                mac: mac?.toUpperCase(),
                interval,
                ...extraParams,
            },
        });
        return data ?? {};
    },

    async getWanData(mac, interval, extraParams = {}) {
        const data = await requestV1('wan', {
            queryParams: {
                mac: mac?.toUpperCase(),
                interval,
                ...extraParams,
            },
        });
        return data.raw ?? {};
    },

    async getAlertsData(mac, interval, extraParams = {}) {
        const data = await requestV1('alerts', {
            queryParams: {
                mac: mac?.toUpperCase(),
                interval,
                ...extraParams,
            },
        });
        return data ?? {};
    },

    async getCrashReports(mac, interval, extraParams = {}) {
        const data = await requestV1('crash-report', {
            queryParams: {
                mac: mac?.toUpperCase(),
                interval,
                ...extraParams,
            },
        });
        return data ?? {};
    },

    async getRestarts(mac, interval, extraParams = {}) {
        const data = await requestV1('restarts', {
            queryParams: {
                mac: mac?.toUpperCase(),
                interval,
                ...extraParams,
            },
        });
        return data ?? {};
    },

    async getStatusEvents(mac, interval, extraParams = {}) {
        const data = await requestV1('status', {
            queryParams: {
                mac: mac?.toUpperCase(),
                interval,
                ...extraParams,
            },
        });
        return data ?? {};
    },

    async impersonateRole(role) {
        if (!role) {
            throw new Error('role is required');
        }
        return requestPg('auth/impersonate', {
            method: 'POST',
            body: { role },
        });
    },

    async revertRole() {
        return requestPg('auth/impersonate/revert', {
            method: 'POST',
        });
    },

    async getReportReasons() {
        const data = await requestPg('report-reasons');
        return data?.report_reasons ?? [];
    },

    async getUserProblemAreas() {
        const data = await requestPg('user-problem-areas');
        return data?.user_problem_areas ?? [];
    },

    async getUserProblemServices(userProblemId) {
        const queryParams = userProblemId ? { user_problem_id: String(userProblemId) } : undefined;
        const data = await requestPg('user-problem-services', { queryParams });
        return data?.user_problem_services ?? [];
    },

    async listUserIssueReports() {
        const data = await requestPg('user-issue-reports');
        return data?.user_issue_reports ?? [];
    },

    async createUserIssueReport(payload) {
        if (!payload || typeof payload !== 'object') {
            throw new Error('payload is required');
        }
        return requestPg('user-issue-reports', {
            method: 'POST',
            body: payload,
        });
    },

    async deleteUserIssueReport(reportId) {
        if (!reportId) {
            throw new Error('reportId is required');
        }
        return requestPg(`user-issue-reports/${encodeURIComponent(reportId)}`, {
            method: 'DELETE',
        });
    },

    async getUserAps(userId, band) {
        if (!userId) {
            return [];
        }

        try {
            const data = await requestPg(`users/${encodeURIComponent(userId)}/aps`, {
                queryParams: band ? { band: normalizeBand(band) } : undefined,
            });
            return data?.items ?? [];
        } catch (error) {
            if (error?.status === 404) {
                return [];
            }
            throw error;
        }
    },

    async upsertUserAp(userId, payload, isUpdate = false) {
        if (!userId) {
            throw new Error('userId is required');
        }

        const normalized = normalizeApPayload(payload);
        if (!normalized.band) {
            throw new Error('band is required');
        }

        const endpoint = isUpdate
            ? `users/${encodeURIComponent(userId)}/aps/${encodeURIComponent(normalized.ap_id)}`
            : `users/${encodeURIComponent(userId)}/aps`;
        const method = isUpdate ? 'PUT' : 'POST';

        return requestPg(endpoint, {
            method,
            body: normalized,
        });
    },

    async deleteUserAp(userId, band, apId) {
        if (!userId) {
            throw new Error('userId is required');
        }

        const normalizedBand = normalizeBand(band);
        if (!normalizedBand) {
            throw new Error('band is required');
        }

        return requestPg(`users/${encodeURIComponent(userId)}/aps/${encodeURIComponent(apId)}`, {
            method: 'DELETE',
            queryParams: { band: normalizedBand },
        });
    },

    async lookupMac(macAddress) {
        if (!macAddress) {
            throw new Error('mac address is required');
        }

        const normalized = String(macAddress).trim();
        if (!normalized) {
            throw new Error('mac address is required');
        }

        return requestV1(`mac-lookup/${encodeURIComponent(normalized)}`);
    },

    async getApiVersion() {
        return send(API_ROOT, 'version');
    },

    async getUserPreferences(userId) {
        const endpoint = userId
            ? `users/${encodeURIComponent(userId)}/user-preferences`
            : 'user-preferences';
        const data = await requestPg(endpoint);
        return data?.user_preferences ?? [];
    },

    async createUserPreference(payload) {
        return requestPg('user-preferences', {
            method: 'POST',
            body: payload,
        });
    },

    async updateUserPreference(id, payload) {
        return requestPg(`user-preferences/${encodeURIComponent(id)}`, {
            method: 'PUT',
            body: payload,
        });
    },

    async deleteUserPreference(id) {
        return requestPg(`user-preferences/${encodeURIComponent(id)}`, {
            method: 'DELETE',
        });
    },

    async getAllWlanAppMetrics() {
        const data = await requestPg('wlan-app-metrics');
        return data?.metrics ?? [];
    },

    async getUserWlanAppMetrics(userId) {
        if (!userId) {
            throw new Error('userId is required');
        }
        const data = await requestPg(`users/${encodeURIComponent(userId)}/wlan-app-metrics`);
        return data?.metrics ?? [];
    },



    async getDiscoveryReport(scannerMac, interval, extraParams = {}) {
        if (!scannerMac) {
            throw new Error('scannerMac is required');
        }
        return requestPg(`devices/${encodeURIComponent(scannerMac.toUpperCase())}/discovery`, {
            queryParams: {
                interval,
                ...extraParams,
            },
        });
    },

    async getHeartbeatHistory(interval) {
        return requestPg('devices/heartbeats/history', {
            queryParams: { interval },
        });
    },

    async chatWithAgent(message, { includeDebug = false, history = [] } = {}) {
        if (!message) {
            throw new Error('Message is required');
        }
        return requestV1('agent/chat', {
            method: 'POST',
            body: { message, include_debug: includeDebug, history },
        });
    },

    async getActionRequests() {
        return requestV1('action-agent/requests');
    },

    async getActionRequestUnreadCount() {
        return requestV1('action-agent/requests/unread-count');
    },

    async getActionConversation(actionId) {
        return requestV1(`action-agent/requests/${actionId}`);
    },

    async replyToActionRequest(actionId, message, attachments = null) {
        const body = { message };
        if (attachments) body.attachments = attachments;
        return requestV1(`action-agent/requests/${actionId}/reply`, {
            method: 'POST',
            body,
        });
    },

    async uploadActionImage(actionId, file) {
        const formData = new FormData();
        formData.append('file', file);
        const token = getAccessToken();
        const response = await fetch(`${API_V1_BASE}/action-agent/requests/${actionId}/upload`, {
            method: 'POST',
            headers: token ? { Authorization: `Bearer ${token}` } : {},
            body: formData,
        });
        return parseResponse(response);
    },
};